import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from "date-fns/locale";
import {
	MessageCircle,
	Loader2,
	AlertCircle,
	RotateCw,
	MoreVertical,
	Trash2,
	Pencil,
	Play,
	Pause,
	Download,
	Podcast as PodcastIcon,
} from "lucide-react";
import { toast } from "sonner";
import { PodcastService } from "@/lib/podcast/podcast.service";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useBottomSheetGesture } from "@/components/player/hooks/useBottomSheetGesture.hook";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { FeedbackModal } from "@/components/feedbackModal.component";
import type { PodWorkspace } from "@/lib/podWorkspace/podWorkspace.service";
import type { Podcast } from "@/lib/podcast/podcast.service";
import {
	isPodcastAwaitingCostApproval,
	isPodcastPipelineProcessing,
} from "@/features/podWorkspace/podcastPipelineStatus.util";
import { useState, useEffect } from "react";
import { FUNNY_LOADING_MESSAGES } from "../podWorkspace.types";
import { useTranslation } from "react-i18next";
import {
	getWorkspaceActions,
	usePodWorkspaceStore,
} from "@/lib/podWorkspace/podWorkspace.store";

interface PodWorkspaceCardProps {
	workspace: PodWorkspace;
	podcast?: Podcast | null;
	isRegenerating?: boolean;
	isCurrentlyPlaying?: boolean;
	isPlayerPaused?: boolean;
	onPlayPodcast?: (podcastId: string) => void;
	onRegenerateAudio?: (podcastId: string) => void;
	onOpenChat?: (workspaceId: string) => void;
	onRetryPodcast?: (podcastId: string) => void;
	onWorkspaceDeleted?: () => void; // Callback after successful deletion for refetching
	/** "dropdown" (default) or "bottomSheet" - bottom sheet preferred for mobile/dashboard */
	menuVariant?: "dropdown" | "bottomSheet";
}

/**
 * This is the pod workspace card component.
 * It is used to display a pod workspace card.
 * It is a card that displays the pod workspace title, progress, and actions.
 */
export const PodWorkspaceCard = ({
	workspace,
	podcast,
	isRegenerating = false,
	isCurrentlyPlaying = false,
	isPlayerPaused = true,
	onPlayPodcast,
	onOpenChat,
	onRetryPodcast,
	onWorkspaceDeleted,
	menuVariant = "dropdown",
}: PodWorkspaceCardProps) => {
	const { t, i18n } = useTranslation();
	const hasPodcast = podcast !== null && podcast !== undefined;

	// Get the appropriate date-fns locale based on current language
	const dateLocale = i18n.language === "de" ? de : enUS;
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [renameDialogOpen, setRenameDialogOpen] = useState(false);
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);

	// Bottom sheet gesture for menuVariant="bottomSheet"
	const { dragState, bind } = useBottomSheetGesture({
		onClose: () => setIsMenuOpen(false),
		closeThreshold: 150,
		disabled: !isMenuOpen || menuVariant !== "bottomSheet",
	});

	// Get deletion state from store
	const isDeletingWorkspace = usePodWorkspaceStore(
		(state) => state.isDeletingWorkspace,
	);
	const isDeleting = isDeletingWorkspace === workspace.id;
	const updateWorkspace = usePodWorkspaceStore(
		(state) => state.updateWorkspace,
	);

	// Get the latest workspace from store to ensure we have the most up-to-date title
	const storeWorkspace = usePodWorkspaceStore((state) =>
		state.workspaces.find((ws) => ws.id === workspace.id),
	);
	// Use store workspace if available, otherwise fall back to prop (for backwards compatibility)
	const displayWorkspace = storeWorkspace || workspace;
	const [newTitle, setNewTitle] = useState(displayWorkspace.title);

	// Update newTitle when workspace title changes
	useEffect(() => {
		setNewTitle(displayWorkspace.title);
	}, [displayWorkspace.title]);
	// Determine playback state
	const progress = podcast?.progress;
	const progressPercentage = progress?.progressPercentage || 0;
	const isProcessing = isPodcastPipelineProcessing(podcast) || isRegenerating;
	const isAwaitingCostApproval = isPodcastAwaitingCostApproval(podcast);
	const isFailed = podcast?.status === "FAILED";
	const isCompleted = podcast?.status === "COMPLETED";
	const canDownload = hasPodcast && isCompleted;
	const retryCount = podcast?.retryCount || 0;
	const canRetry = isFailed && retryCount < 3;

	// Rotate through funny loading messages while processing
	const [loadingMessage, setLoadingMessage] = useState(
		FUNNY_LOADING_MESSAGES[
			Math.floor(Math.random() * FUNNY_LOADING_MESSAGES.length)
		],
	);

	useEffect(() => {
		if (isProcessing) {
			let messageIndex = 0;
			setLoadingMessage(
				FUNNY_LOADING_MESSAGES[
					Math.floor(Math.random() * FUNNY_LOADING_MESSAGES.length)
				],
			);

			const interval = setInterval(() => {
				messageIndex = (messageIndex + 1) % FUNNY_LOADING_MESSAGES.length;
				setLoadingMessage(FUNNY_LOADING_MESSAGES[messageIndex]);
			}, 4000); // Change message every 4 seconds

			return () => clearInterval(interval);
		}
	}, [isProcessing]);

	// Determine RAG (Chat) state
	const embeddingStatuss = podcast?.embeddingStatus;
	const isRagFailed = embeddingStatuss?.status === "FAILED";
	const isRagProcessing =
		embeddingStatuss?.status === "PROCESSING" ||
		embeddingStatuss?.status === "PENDING";
	const isChatDisabled = hasPodcast && (isRagFailed || isRagProcessing);

	const handlePlayPodcast = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (
			podcast?.id &&
			onPlayPodcast &&
			!isFailed &&
			!isProcessing &&
			!isAwaitingCostApproval
		) {
			onPlayPodcast(podcast.id);
		}
	};

	const handleOpenChat = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (onOpenChat && !isChatDisabled && !isFailed) {
			onOpenChat(workspace.id);
		}
	};

	const handleRetry = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (podcast?.id && onRetryPodcast) {
			onRetryPodcast(podcast.id);
		}
	};

	const handleDownload = async () => {
		if (!podcast?.id || !canDownload || isDownloading) return;
		setIsDownloading(true);
		const toastId = toast.loading(t("sidebar.downloadStarted"));
		try {
			const { url } = await PodcastService.getPodcastAudioUrl(podcast.id);
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Failed to fetch audio: ${response.status}`);
			}
			const blob = await response.blob();
			const objectUrl = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = objectUrl;
			link.download = `${displayWorkspace.title || "podcast"}.mp3`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(objectUrl);
			toast.success(t("sidebar.downloadSuccess"), { id: toastId });
		} catch (error) {
			console.error("Failed to download podcast:", error);
			toast.error(t("sidebar.downloadError"), { id: toastId });
		} finally {
			setIsDownloading(false);
		}
	};

	const handleDeleteWorkspace = async () => {
		try {
			await getWorkspaceActions().deleteWorkspace(
				workspace.id,
				onWorkspaceDeleted,
			);
			setDeleteDialogOpen(false);
		} catch (error) {
			// Error is already handled by the store with toast
			console.error("Failed to delete workspace:", error);
		}
	};

	const openDeleteDialog = () => {
		setDeleteDialogOpen(true);
	};

	const openRenameDialog = () => {
		setNewTitle(displayWorkspace.title);
		setRenameDialogOpen(true);
	};

	const handleRenameWorkspace = async () => {
		if (!newTitle.trim() || newTitle.trim() === displayWorkspace.title) {
			setRenameDialogOpen(false);
			return;
		}

		try {
			await updateWorkspace(
				workspace.id,
				{ title: newTitle.trim() },
				async () => {
					// Refetch workspaces after successful rename to ensure UI is in sync
					await getWorkspaceActions().fetchWorkspacesWithPodcasts();
				},
			);
			setRenameDialogOpen(false);
		} catch (error) {
			// Error is already handled by the store with toast
			console.error("Failed to rename workspace:", error);
		}
	};

	// Format duration from milliseconds to "M:SS"
	const formatDuration = (ms: number) => {
		const totalSeconds = Math.floor(ms / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes}:${seconds.toString().padStart(2, "0")}`;
	};

	// Build subtitle parts for mobile card
	const chapterCount = podcast?.chapters?.length || 0;
	const durationMs = podcast?.length || 0;

	// Status badge — only show for failed state
	// Ready = default, no badge needed. Processing = animated border is enough.
	const statusBadge = isFailed
		? { label: t("podWorkspace.statusFailed"), variant: "destructive" as const }
		: null;

	/**
	 * Dropdown menu (uncontrolled — each card instance manages its own open state
	 * independently so the hidden mobile/desktop card doesn't spawn a phantom portal).
	 */
	const dropdownMenu = (
		<div
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}
			onPointerDown={(e) => e.stopPropagation()}
		>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7 hover:bg-accent z-20 shrink-0 pointer-events-auto"
					>
						<MoreVertical className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align="end"
					className="z-20"
					onClick={(e) => e.stopPropagation()}
				>
					{canDownload && (
						<DropdownMenuItem
							onSelect={() => handleDownload()}
							disabled={isDownloading}
							className="cursor-pointer"
						>
							{isDownloading ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Download className="mr-2 h-4 w-4" />
							)}
							{isDownloading
								? t("sidebar.downloading")
								: t("sidebar.downloadPodcast")}
						</DropdownMenuItem>
					)}
					<DropdownMenuItem
						onSelect={() => openRenameDialog()}
						className="cursor-pointer"
					>
						<Pencil className="mr-2 h-4 w-4" />
						{t("sidebar.renameWorkspace")}
					</DropdownMenuItem>
					<DropdownMenuItem
						onSelect={() => openDeleteDialog()}
						className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer"
					>
						<Trash2 className="mr-2 h-4 w-4" />
						{t("sidebar.deleteWorkspace")}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);

	/**
	 * Bottom-sheet trigger button only — the Sheet itself is rendered once
	 * outside both cards so it doesn't double-mount from the hidden card.
	 */
	const bottomSheetTrigger = (
		<div
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}
			onPointerDown={(e) => e.stopPropagation()}
		>
			<Button
				variant="ghost"
				size="icon"
				className="h-7 w-7 hover:bg-accent z-20 shrink-0 pointer-events-auto"
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setIsMenuOpen(true);
				}}
			>
				<MoreVertical className="h-4 w-4" />
			</Button>
		</div>
	);

	const menuElement =
		menuVariant === "bottomSheet" ? bottomSheetTrigger : dropdownMenu;

	return (
		<>
			{/* ─── Mobile Card: compact horizontal row ─── */}
			<Card
				className={`md:hidden relative w-full p-3 cursor-pointer transition-all duration-200 active:scale-[0.98] group overflow-visible bg-white/[0.05] backdrop-blur-lg shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_2px_8px_rgba(0,0,0,0.15)] ${
					isProcessing
						? "border-transparent"
						: isCurrentlyPlaying
							? "border-primary/40 ring-1 ring-primary/20"
							: "border-white/[0.1] active:border-primary/40"
				}`}
				onClick={(e) => {
					if (e.defaultPrevented) return;
					handlePlayPodcast(e);
				}}
			>
				{/* Processing border animation */}
				{isProcessing && (
					<>
						<div className="absolute -inset-[3px] rounded-xl overflow-hidden pointer-events-none z-[1]">
							<div
								className="absolute top-1/2 left-1/2 w-[200%] aspect-square animate-border-rotate"
								style={{
									background:
										"conic-gradient(from 0deg, transparent 0%, transparent 65%, var(--color-primary) 78%, var(--color-primary) 83%, transparent 95%, transparent 100%)",
								}}
							/>
						</div>
						<div className="absolute -inset-px rounded-xl liquid-glass pointer-events-none z-[2]" />
					</>
				)}

				<div className="flex items-center gap-3 relative z-[5]">
					{/* Podcast icon */}
					<div className="shrink-0 p-2.5 rounded-xl bg-white/[0.06] border border-white/[0.06]">
						{isCurrentlyPlaying && !isPlayerPaused ? (
							<Play className="h-5 w-5 text-primary" />
						) : (
							<PodcastIcon className="h-5 w-5 text-muted-foreground/80" />
						)}
					</div>

					{/* Content */}
					<div className="flex-1 min-w-0 space-y-1.5">
						{/* Title row + status badge */}
						<div className="flex items-center gap-2">
							<h3 className="text-sm font-medium leading-tight text-foreground/95 truncate">
								{displayWorkspace.title}
							</h3>
							{statusBadge && (
								<Badge
									variant={statusBadge.variant}
									className="shrink-0 text-[10px] px-1.5 py-0 h-5 font-medium"
								>
									{statusBadge.label}
								</Badge>
							)}
						</div>

						{/* Subtitle: chapters · duration or funny loading message */}
						{hasPodcast && !isProcessing && !isAwaitingCostApproval && (
							<p className="text-[11px] text-muted-foreground/80 leading-none">
								{chapterCount > 0 && (
									<>
										{t("podWorkspace.chaptersCount", { count: chapterCount })}
										{durationMs > 0 && " · "}
									</>
								)}
								{durationMs > 0 && formatDuration(durationMs)}
							</p>
						)}
						{hasPodcast && isAwaitingCostApproval && (
							<p className="text-[11px] text-muted-foreground/80 leading-none">
								{t("podWorkspace.awaitingCostApproval")}
							</p>
						)}
						{hasPodcast && isProcessing && (
							<p
								key={loadingMessage}
								className="text-[11px] text-muted-foreground/80 leading-none animate-in fade-in slide-in-from-bottom-1 duration-500"
							>
								{loadingMessage}
							</p>
						)}

						{/* Progress bar */}
						{hasPodcast &&
							!isProcessing &&
							!isFailed &&
							!isAwaitingCostApproval && (
								<Progress value={progressPercentage} className="h-1.5" />
							)}
					</div>

					{/* Menu */}
					{menuElement}
				</div>
			</Card>

			{/* ─── Desktop Card: original vertical layout ─── */}
			<Card
				className={`hidden md:flex relative min-h-[9rem] w-80 shrink-0 p-4 cursor-pointer transition-all duration-200 hover:shadow-lg active:shadow-md flex-col group overflow-visible bg-white/[0.04] backdrop-blur-lg shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05),0_2px_12px_rgba(0,0,0,0.12)] ${
					isProcessing
						? "border-transparent"
						: isCurrentlyPlaying
							? "border-primary/40 ring-1 ring-primary/20"
							: "border-white/[0.08] hover:border-white/[0.15] active:border-primary/40"
				}`}
				onClick={(e) => {
					if (e.defaultPrevented) return;
					handlePlayPodcast(e);
				}}
			>
				{/* Animated border snake loading effect */}
				{isProcessing && (
					<>
						<div className="absolute -inset-[3px] rounded-xl overflow-hidden pointer-events-none z-[1]">
							<div
								className="absolute top-1/2 left-1/2 w-[200%] aspect-square animate-border-rotate"
								style={{
									background:
										"conic-gradient(from 0deg, transparent 0%, transparent 65%, var(--color-primary) 78%, var(--color-primary) 83%, transparent 95%, transparent 100%)",
								}}
							/>
						</div>
						<div className="absolute -inset-[6px] rounded-2xl overflow-hidden pointer-events-none z-0 blur-xs opacity-10">
							<div
								className="absolute top-1/2 left-1/2 w-[200%] aspect-square animate-border-rotate"
								style={{
									background:
										"conic-gradient(from 0deg, transparent 0%, transparent 65%, var(--color-primary) 78%, var(--color-primary) 83%, transparent 95%, transparent 100%)",
								}}
							/>
						</div>
						<div className="absolute -inset-px rounded-xl liquid-glass pointer-events-none z-[2]" />
					</>
				)}
				{/* 3-dot menu - Absolute positioned, z-10 to sit above the z-[5] content layer */}
				<div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto">
					{menuElement}
				</div>

				{/* Header Section */}
				<div className="flex flex-col h-full justify-between relative z-[5]">
					<div className="space-y-3">
						<div className="flex items-start justify-between gap-3 mr-5">
							<h3 className="font-semibold text-md leading-tight text-foreground line-clamp-2">
								{displayWorkspace.title}
							</h3>
							<div className="flex items-center gap-1 shrink-0">
								{isCurrentlyPlaying && (
									<Badge
										variant="outline"
										className={`shrink-0 border-primary/50 rounded-full ${!isPlayerPaused ? "animate-pulse" : ""}`}
									>
										{isPlayerPaused ? (
											<Pause className="h-3 w-3" />
										) : (
											<Play className="h-3 w-3" />
										)}
									</Badge>
								)}

								{isFailed && (
									<div className="flex items-center gap-1">
										<Badge
											variant="destructive"
											className="shrink-0 gap-1.5 px-2 py-0.5"
										>
											<AlertCircle className="h-3 w-3" />
											<span className="text-[10px] font-medium uppercase tracking-wider">
												Failed
											</span>
										</Badge>
										{canRetry && (
											<Button
												variant="ghost"
												size="sm"
												className="h-6 px-2 text-[10px] gap-1 hover:bg-destructive/10 hover:text-destructive"
												onClick={handleRetry}
											>
												<RotateCw className="h-3 w-3" />
												Retry ({retryCount}/3)
											</Button>
										)}
									</div>
								)}
								{!isProcessing && !isFailed && isRagFailed && (
									<Badge
										variant="destructive"
										className="shrink-0 gap-1.5 px-2 py-0.5"
									>
										<AlertCircle className="h-3 w-3" />
										<span className="text-[10px] font-medium uppercase tracking-wider">
											Chat Error
										</span>
									</Badge>
								)}
							</div>
						</div>

						{/* Progress Section */}
						{hasPodcast &&
							!isProcessing &&
							!isFailed &&
							!isAwaitingCostApproval && (
								<div className="space-y-2">
									<Progress value={progressPercentage} className="h-1.5" />
								</div>
							)}
						{hasPodcast && isAwaitingCostApproval && (
							<p className="text-xs text-muted-foreground leading-snug">
								{t("podWorkspace.awaitingCostApproval")}
							</p>
						)}
						{hasPodcast && isProcessing && (
							<div>
								<div className="flex items-center justify-between flex-col text-muted-foreground gap-1 overflow-hidden">
									<span className="text-xs font-medium">
										{t("podWorkspace.processingMayTakeFewMinutes")}
									</span>
									<span
										key={loadingMessage}
										className="animate-in text-xs font-thin slide-in-from-bottom-2 text-muted-foreground fade-in duration-700"
									>
										{loadingMessage}
									</span>
								</div>
							</div>
						)}
					</div>

					{/* Footer Section */}
					<div className="flex items-center justify-between text-xs text-muted-foreground font-medium mt-auto">
						{/* Footer Actions */}
						<div className="flex items-center justify-between mb-0">
							<Button
								variant="ghostText"
								size="sm"
								className={`cursor-pointer relative z-10 text-muted-foreground hover:text-foreground p-0 gap-2 h-6 text-sm ${
									isChatDisabled ? "cursor-not-allowed opacity-70" : ""
								}`}
								onClick={handleOpenChat}
								disabled={isChatDisabled}
							>
								{isRagProcessing ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : isRagFailed ? (
									<AlertCircle className="h-4 w-4" />
								) : (
									<MessageCircle className="h-4 w-4" />
								)}
								<span>
									{isRagProcessing
										? t("chat.preparingChat")
										: isRagFailed
											? t("chat.chatUnavailable")
											: t("player.openChat")}
								</span>
							</Button>
						</div>

						{progress?.lastListenedAt && (
							<span>
								{formatDistanceToNow(new Date(progress.lastListenedAt), {
									addSuffix: true,
									locale: dateLocale,
								})}
							</span>
						)}
					</div>
				</div>
			</Card>

			{/* Shared Dialogs (outside both cards, inside fragment) */}

			{/* Delete Confirmation Dialog */}
			<Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<DialogContent onClick={(e) => e.stopPropagation()}>
					<DialogHeader>
						<DialogTitle>{t("sidebar.deleteConfirmTitle")}</DialogTitle>
						<DialogDescription className="space-y-2">
							{t("sidebar.deleteConfirmMessage", {
								title: workspace.title,
							})}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="justify-between">
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setDeleteDialogOpen(false);
								// Delay opening feedback modal to ensure delete dialog overlay is removed
								setTimeout(() => {
									setIsFeedbackModalOpen(true);
								}, 100);
							}}
							className="text-primary underline cursor-pointer text-sm font-medium mt-2 block mr-auto"
						>
							{t("sidebar.deleteFeedbackLink")}
						</button>
						<div className="flex flex-col-reverse gap-2 items-stretch justify-end sm:flex-row sm:items-center sm:gap-2">
							<Button
								variant="outline"
								onClick={() => setDeleteDialogOpen(false)}
								disabled={isDeleting}
							>
								{t("sidebar.cancel")}
							</Button>
							<Button
								variant="destructive"
								onClick={handleDeleteWorkspace}
								disabled={isDeleting}
							>
								{isDeleting ? t("sidebar.deleting") : t("sidebar.delete")}
							</Button>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Rename Dialog */}
			<Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
				<DialogContent onClick={(e) => e.stopPropagation()}>
					<DialogHeader>
						<DialogTitle>{t("sidebar.renameWorkspaceTitle")}</DialogTitle>
						<DialogDescription>
							{t("sidebar.renameWorkspaceDescription")}
						</DialogDescription>
					</DialogHeader>
					<div className="py-4">
						<Input
							value={newTitle}
							onChange={(e) => setNewTitle(e.target.value)}
							placeholder={t("sidebar.workspaceTitlePlaceholder")}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									handleRenameWorkspace();
								}
							}}
							autoFocus
						/>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setRenameDialogOpen(false)}
						>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleRenameWorkspace}
							disabled={!newTitle.trim() || newTitle.trim() === workspace.title}
						>
							{t("common.save")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Bottom-sheet menu (rendered once, outside both cards, to avoid double portal) */}
			{menuVariant === "bottomSheet" && (
				<Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
					<SheetContent
						side="bottom"
						className="rounded-t-3xl border-t border-foreground/[0.06] pb-8 liquid-glass-bold !rounded-b-none [&>:last-child]:hidden"
						onClick={(e) => e.stopPropagation()}
						style={{
							transform: dragState.isDragging
								? `translateY(${dragState.y}px)`
								: undefined,
							opacity: dragState.isDragging ? dragState.opacity : 1,
							transition: dragState.isDragging
								? "none"
								: "transform 0.3s ease-out, opacity 0.3s ease-out",
						}}
					>
						<div
							className="flex flex-col items-center pt-2 pb-2 cursor-grab active:cursor-grabbing touch-none"
							{...bind()}
						>
							<div className="w-12 h-1.5 bg-foreground/15 rounded-full mb-4" />
						</div>
						<div className="space-y-1 px-4">
							{canDownload && (
								<button
									type="button"
									disabled={isDownloading}
									className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium hover:bg-accent transition-colors disabled:opacity-60"
									onClick={() => {
										setIsMenuOpen(false);
										void handleDownload();
									}}
								>
									{isDownloading ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<Download className="h-4 w-4" />
									)}
									{isDownloading
										? t("sidebar.downloading")
										: t("sidebar.downloadPodcast")}
								</button>
							)}
							<button
								type="button"
								className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium hover:bg-accent transition-colors"
								onClick={() => {
									setIsMenuOpen(false);
									openRenameDialog();
								}}
							>
								<Pencil className="h-4 w-4" />
								{t("sidebar.renameWorkspace")}
							</button>
							<button
								type="button"
								className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
								onClick={() => {
									setIsMenuOpen(false);
									openDeleteDialog();
								}}
							>
								<Trash2 className="h-4 w-4" />
								{t("sidebar.deleteWorkspace")}
							</button>
						</div>
					</SheetContent>
				</Sheet>
			)}

			{/* Feedback Modal */}
			<FeedbackModal
				open={isFeedbackModalOpen}
				onOpenChange={setIsFeedbackModalOpen}
			/>
		</>
	);
};
